import { Site } from '@pubstack/common/src/site'
import { SiteSpaConfig } from '@pubstack/common/src/kleanadsScopeConfig'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, useCallback, useState } from 'react'
import { Control, Controller, FieldErrors, useForm, useWatch } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'
import { useUpdateSpaConfig } from '~/api/adm/site-configs'
import { useUpdateSite } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { CopyButton } from '~/components/CopyButton'
import { Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Skeleton } from '~/components/Skeleton'
import { useToast } from '~/components/Toast/useToasts'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { MEDIA_NAME_IS_READ_ONLY } from '../EditSiteFlyout'
import { hasPbjsVariableChanged, PBJS_VARIABLE_WILL_CHANGE, PBJS_VARIABLE_WILL_CHANGE_TITLE, pbjsVariableLockOf, pbjsVariableNamePayload, SitePbjsVariableInput } from '../SitePbjsVariableInput'
import { SaveConfirmationChallenge, SiteSaveConfirmationModal } from '../SiteSaveConfirmationModal'
import { siteProductOf } from '../siteProduct'
import { AdmSitePageContext, isAdmContext, PbsSitePageContext } from './useSitePageContext'

/** `spaConfig` is One's, and only on a media One is really running: a Node media has none and sends none. */
type ConfigurationForm = Pick<Site, 'name' | 'id' | 'pbjsVariableName'> & { spaConfig?: SiteSpaConfig }

const SPA_IS_OFF = 'Turn SPA on to choose how a new page is detected.'

/**
 * A media's own settings, on the media's own pages.
 *
 * The flyout over the list already held these, and still does — it is what a row click opens, and the
 * quickest way to correct a name. This is the same settings reached the other way: from the pen, on the
 * media you are already looking at, beside its stacks and its activation. The fields are deliberately the
 * same two, so neither route teaches something the other contradicts.
 *
 * SPA is the exception, and belongs only here: it is One's own page-detection setting, served by One's own
 * endpoint, so a Node media has no SPA to configure and its tab does not pretend otherwise.
 */
export const MediaConfigurationPage: FunctionComponent = () => {
  const context = useOutletContext<PbsSitePageContext | AdmSitePageContext>()
  const { site, scopeId, isLoading } = context
  const spaConfig = isAdmContext(context) ? context.spaConfig : undefined

  const user = useAuthenticatedUser()
  const modal = useGlobalModal()
  const toast = useToast()
  const [nameError, setNameError] = useState<string>('')

  // The same gate the flyout applies, for the same reason: the name is what every dashboard, alert and
  // export calls this media, so it is not a `user`'s to change under the rest of the scope.
  const canRenameMedia = user.getScopeRole() === 'owner' || user.isAdmin
  const currentProduct = siteProductOf(site)
  const currentPbjsVariableName = site.pbjsVariableName ?? ''
  // One serves its own Prebid build under `kleanadsPbjs`; on a One media the variable is nobody's to rename.
  const pbjsVariableLock = pbjsVariableLockOf({ isManagedByOne: currentProduct === 'adm', isOwner: canRenameMedia })
  const canEditPbjsVariableName = !pbjsVariableLock

  // `adManagementEnabled` as well as the config itself: the One media pages are reachable before One is
  // activated on the media — half the tabs up there render disabled for exactly that state, and the
  // Activation tab answers it with "This media is not on One yet." A media in that state has no One to
  // configure, so it gets no SPA cards either.
  const hasSpa = !!spaConfig && !!site.adManagementEnabled

  const storedValues = { name: site.name, id: site.id, pbjsVariableName: currentPbjsVariableName, spaConfig }

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid, isDirty },
  } = useForm<ConfigurationForm>({
    values: storedValues,
    mode: 'onChange',
  })

  const siteName = useWatch({ control, name: 'name' })

  const { mutateAsync: updateSite, isPending: isSiteUpdatePending } = useUpdateSite(scopeId, site.id, {
    successToast: (
      <>
        <strong>{siteName}</strong> was successfully updated.
      </>
    ),
  })
  // No toast of its own: SPA now leaves on the same Save as the name and the variable, and one click that
  // changed both should say so once.
  const { mutateAsync: updateSpaConfig } = useUpdateSpaConfig(site.id)

  const handleUpdateSite = async () => {
    if (!scopeId || !site.id || isSiteUpdatePending) return
    try {
      const typedName = getValues('name').trim()
      if (!typedName) return

      // The media minus its stored Prebid variable: the route returns the field now — it is what fills the
      // form — and spreading it back would put the key in every save, which is what the payload avoids.
      const { pbjsVariableName: _stored, ...siteWithoutVariable } = site
      // SPA is One's own endpoint, not a field on the media, so one Save is two writes. They touch nothing
      // in common, so neither has to wait on the other.
      const typedSpaConfig = getValues('spaConfig')
      await Promise.all([
        updateSite({
          ...siteWithoutVariable,
          // The disabled field is the affordance; keeping the stored name is the invariant.
          name: canRenameMedia ? typedName : site.name,
          ...pbjsVariableNamePayload(getValues('pbjsVariableName'), currentPbjsVariableName, canEditPbjsVariableName),
        }),
        ...(hasSpa && typedSpaConfig ? [updateSpaConfig(typedSpaConfig)] : []),
      ])
      setNameError('')
    } catch (errors) {
      if (errors.response?.data?.message === 'Conflict data') {
        setNameError('A media with this name already exists.')
      } else {
        setNameError('')
        toast.alert(errors.message)
      }
    }
  }

  /** Re-pointing the Prebid variable can cut a media's data collection, so it is typed back before it saves. */
  const handleSave = async () => {
    const typedVariable = getValues('pbjsVariableName')?.trim() ?? ''
    if (!hasPbjsVariableChanged(typedVariable, currentPbjsVariableName, canEditPbjsVariableName)) {
      await handleUpdateSite()
      return
    }
    const challenges: SaveConfirmationChallenge[] = [{ key: 'pbjsVariable', title: PBJS_VARIABLE_WILL_CHANGE_TITLE, description: PBJS_VARIABLE_WILL_CHANGE, phrase: typedVariable }]
    modal.open(SiteSaveConfirmationModal, {
      title: 'Save changes',
      challenges,
      confirmLabel: 'Save changes',
      trackingLabel: 'pbs-edit-site-save-confirm',
      onConfirm: handleUpdateSite,
    })
  }

  const getErrorMessage = useCallback(
    (errors: FieldErrors<ConfigurationForm>) => {
      if (errors?.name?.type === 'required') return 'A media name is required'
      if (errors?.name?.type === 'pattern') return 'A media name cannot only be whitespaces'
      return nameError || undefined
    },
    [nameError]
  )

  if (isLoading) return <Skeleton bigger />

  return (
    <div className={'mt-5 flex max-w-2xl flex-col gap-8'}>
      <form className={'flex flex-col gap-6'}>
        {/* A disabled field dispatches no mouse events, so the padlock — which is not disabled — is what the
            reader can actually hover to find out why the field is shut. */}
        <Tooltip title={MEDIA_NAME_IS_READ_ONLY} disabled={canRenameMedia}>
          <Input
            error={getErrorMessage(errors)}
            onChange={() => setNameError('')}
            control={control}
            name={'name'}
            label={'Media name'}
            readOnly={!canRenameMedia}
            disabled={!canRenameMedia}
            iconRight={canRenameMedia ? undefined : 'lock'}
            rules={canRenameMedia ? { required: true, pattern: /.*[^ ].*/ } : {}}
          />
        </Tooltip>
        <div className={'flex items-end gap-3'}>
          <div className={'grow'}>
            <Input readOnly disabled control={control} name={'id'} label={'Media ID'} />
          </div>
          <CopyButton
            onCopy={() =>
              toast.success(
                <>
                  <strong>Media ID</strong> was successfully copied to your clipboard.
                </>
              )
            }
            contentText={site.id}
            trackingLabel={'common-settings-sites-site-id-copy'}
          />
        </div>
        <SitePbjsVariableInput control={control} name={'pbjsVariableName'} currentValue={currentPbjsVariableName} lock={pbjsVariableLock} />
        {hasSpa && <SpaSection control={control} />}
      </form>

      {/* Below everything they commit. SPA used to save on its own control, against One's own endpoint, so
          these buttons sat above it to avoid reading as the thing that committed it. They are that thing
          now — one Save for the media and its SPA alike — so they belong at the end of what they send. */}
      <div className={'flex items-center gap-6'}>
        <Button
          disabled={!isDirty}
          // Spelled out rather than a bare `reset()`: the stored media is the thing to go back to, and it is
          // what the `values` option above keeps the form in step with.
          onClick={() => {
            reset(storedValues)
            setNameError('')
          }}
          iconName={'restore'}
          variant={'tertiary'}
          trackingLabel={'pbs-edit-site-restore'}
        >
          Restore
        </Button>
        <Button disabled={!isDirty || !isValid} onClick={handleSubmit(handleSave)} trackingLabel={'pbs-edit-site-save'}>
          {isSiteUpdatePending ? 'Saving changes...' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

/**
 * A setting, what it does, and the switch that sets it, carded up the way the bidder rule settings are: a
 * bordered white row with the switch at its end.
 *
 * The whole card takes the click, which is what makes a readable target out of a 16px switch. Not a
 * `<label>`: that would pass the click on to the switch's own hidden checkbox and flip the value twice.
 */
const SpaCard: FunctionComponent<{ title: string; description: string; value: boolean; disabled?: boolean; onToggle: () => void; trackingLabel: TrackingLabel }> = ({
  title,
  description,
  value,
  disabled,
  onToggle,
  trackingLabel,
}) => (
  <div onClick={disabled ? undefined : onToggle} className={cn('flex flex-col gap-1 rounded-lg border border-platinum bg-white px-4 py-3.5', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
    <div className={'flex items-center gap-3'}>
      <span className={cn(disabled && 'text-ash')}>{title}</span>
      <Toggle className={'ml-auto shrink-0'} value={value} disabled={disabled} aria-label={title} trackingLabel={trackingLabel} />
    </div>
    <p className={'text-sm text-ash'}>{description}</p>
  </div>
)

/**
 * Two cards of the same shape, saved by the page's own Save along with the name and the variable.
 *
 * Page detection used to be a Manual/Auto radio nested under the SPA label, which drew a half-empty grey
 * bar and read as a third thing to decide rather than as the one choice SPA opens up. It has two values,
 * so it is a switch like the card above it.
 *
 * It stays on the page while SPA is off, disabled rather than hidden: a reader deciding whether to turn SPA
 * on can see what it will ask them next, and the section does not change height under the cursor.
 */
const SpaSection: FunctionComponent<{ control: Control<ConfigurationForm> }> = ({ control }) => (
  // One `Controller` over the pair rather than one per switch: `mode` is only ever read with `enabled`, and
  // going through the field is what tells the form it is dirty — without that, a flipped switch is a change
  // the form does not know it has and Save stays disabled on it.
  <Controller
    control={control}
    name={'spaConfig'}
    render={({ field: { value, onChange } }) => {
      const enabled = !!value?.enabled
      const isManual = value?.mode === 'manual'
      return (
        <div className={'flex flex-col gap-3'}>
          <SpaCard
            title={'SPA'}
            description={'Enable if the site is a Single Page Application.'}
            value={enabled}
            onToggle={() => onChange({ enabled: !enabled, mode: value?.mode ?? 'manual' })}
            trackingLabel={enabled ? 'adm-sites-activation-spa-unset' : 'adm-sites-activation-spa-set'}
          />
          {/* A disabled card dispatches no mouse events of its own, so the tooltip rides the wrapper — which
              is not disabled — and is what the reader can actually hover to find out why the switch is shut. */}
          <Tooltip title={SPA_IS_OFF} disabled={enabled}>
            <SpaCard
              title={'Manual page detection'}
              description={'Off, Pubstack detects a new page on its own. On, your site tells it when a new page starts.'}
              value={isManual}
              disabled={!enabled}
              onToggle={() => onChange({ enabled, mode: isManual ? 'auto' : 'manual' })}
              // The labels the radio sent, kept as they were: the series says which detection was chosen,
              // which is the question, and renaming them would split it in two at this release.
              trackingLabel={isManual ? 'adm-sites-activation-spa-auto-detection-set' : 'adm-sites-activation-spa-manual-detection-set'}
            />
          </Tooltip>
        </div>
      )
    }}
  />
)
